
/**
* @summary - Component that Provides Asset Catgeory Creation Page
* @author Aro J
* @since 2024-07-15
*/
import { Box, Grid, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { DefaultCard } from "../../assets/styled-components/card";
import { MasterGraph } from "../../utility/apiURLS";
import { getAuthToken, getCreatedBy, getFacilityId } from "../../utility/tokenUtility";
import CustomDialog from "../commoncomponents/CustomDialog";
import CustomFooter from "../commoncomponents/CustomFooter";
import CustomTextField from "../commoncomponents/CustomTextField";
import PageTitle from "../commoncomponents/PageTitle";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import AddIcon from '@mui/icons-material/Add';

const RackDetails = ({ rows, columns, storeid, fetchrackdetails, isviewmode }) => {
  const [gridAPI, setGridApi] = useState({});
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [rackCode, setRackCode] = useState("");
  const [rackDesc, setRackDesc] = useState("");
  const [errors, setErrors] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const handleOpen = () => {
    setMode("add");
    setRackCode("");
    setRackDesc("");
    setErrors({});
    setOpen(true);
  };

  const handleEdit = (rowData) => {
    setMode("edit");
    setSelectedRow(rowData);
    setRackCode(rowData?.rackno || "");
    setRackDesc(rowData?.rackdesc || "");
    setErrors({});
    setOpen(true);
  };

  const handleDelete = (rowData) => {
    setRowToDelete(rowData);
    setDeleteDialog(true);
  }

  // Accepts either event or raw value from CustomTextField
  const makeChangeHandler = (field) => (evOrVal) => {
    const value =
      typeof evOrVal === "string" || typeof evOrVal === "number"
        ? String(evOrVal)
        : evOrVal?.target?.value ?? "";

    if (field === "RackNo") {
      setRackCode(value);
      setErrors((p) => ({ ...p, rackNo: value.trim() ? "" : "Rack No is mandatory" }));
    } else if (field === "RackDesc") {
      setRackDesc(value);
      setErrors((p) => ({ ...p, rackDesc: value.trim() ? "" : "Rack Description is mandatory" }));
    }
  };

  const validate = () => {
    const next = {
      rackNo: rackCode.trim() ? "" : "Rack No is mandatory",
      rackDesc: rackDesc.trim() ? "" : "Rack Description is mandatory",
    };
    setErrors(next);
    return !next.rackNo && !next.rackDesc;
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const token = getAuthToken("id_token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const handleSave = async () => {
    if (!validate()) return;
    let response;


    if (mode === "add") {
      const payload = {
        id: "",
        rackno: rackCode,
        rackdesc: rackDesc,
        masimswarehousedetailsId: storeid
      }
      response = await axios.post(`${MasterGraph}/api/masimsregiondetails`, payload, config);

    }
    else {
      const payload = {
        id: selectedRow?.id,
        rackno: rackCode,
        rackdesc: rackDesc,
        masimswarehousedetailsId: selectedRow?.masimswarehousedetailsId

      };
      response = await axios.put(`${MasterGraph}/api/masimsregiondetails/${selectedRow?.id}`, payload, config);

    }

    handleClose();
    fetchrackdetails();
  };

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      await axios.delete(
        `${MasterGraph}/api/masimsregiondetails/${rowToDelete?.id}`,
        config
      );
      setDeleteDialog(false);
      setSuccessDialog(true);
      fetchrackdetails();
    } catch (err) {
      console.error("Delete failed", err);
      setDeleteDialog(false);
    }
  };

  return (
    <Box>
      <Grid sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "17px" }}>Rack Details</Typography>
        <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={handleOpen} disabled={isviewmode}>
          Add Row
        </Button>
      </Grid>

      <CustomAgGrid
        columns={columns}
        rows={rows}
        refSet={gridAPI}
        setGridApi={setGridApi}
        handleEdit={isviewmode ? null : handleEdit}
        handleDelete={isviewmode ? null : handleDelete}
      />

      <Dialog open={open} onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}>
        <DialogTitle>{mode === "add" ? "Add Rack Details" : "Edit Rack Details"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6} md={6}>
              <CustomTextField
                width="40%"
                label="Rack No"
                id="rackno"
                name="RackNo"
                value={rackCode || ""}
                onChange={makeChangeHandler("RackNo")}
                error={!!errors.rackNo}
                helperText={errors.rackNo}
                isMandatoryField
              />
            </Grid>
            <Grid item xs={6} md={6}>
              <CustomTextField
                width="50%"
                label="Rack Description"
                id="rackDesc"
                name="RackDesc"
                value={rackDesc || ""}
                onChange={makeChangeHandler("RackDesc")}
                error={!!errors.rackDesc}
                helperText={errors.rackDesc}
                isMandatoryField
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {mode === "add" ? "Add" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog} onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          setDeleteDialog(false);
        }
      }}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent >Are you sure you want to delete?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} variant="outlined">
            No
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="primary">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={successDialog} onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          setSuccessDialog(false);
        }
      }}>
        <DialogContent>Record deleted successfully.</DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessDialog(false)} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};



const CreateStoreDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [warehousecode, setWarehousecode] = useState('');
  const [serviceOptions, setServiceOptions] = useState([]);
  const [personincharge, setPersonincharge] = useState('');
  const [warehousedesc, setWarehousedesc] = useState('');
  const [warehouseaddress1, setWarehouseaddress1] = useState('');
  const [warehouseaddress2, setWarehouseaddress2] = useState('');
  const [warehousecity, setWarehousecity] = useState('');
  const [warehousestate, setWarehousestate] = useState('');
  const [warehousecountry, setWarehousecountry] = useState('');
  const [warehousepostcode, setWarehousepostcode] = useState('');
  const [warehousephoneno, setWarehousephoneno] = useState('');
  const [warehousefaxno, setWarehousefaxno] = useState('');
  const [warehouseemail, setWarehouseemail] = useState('');
  const [id, setId] = useState('');
  const [openValidationDialog, setOpenValidationDialog] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [dialogContent, setDialogContent] = useState('');
  const [isAddNew, setIsAddNew] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [readOnlyEdit, setReadOnlyEdit] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const serviceRef = useRef(null);
  const descRef = useRef(null);
  const [rows, setRows] = useState([]);
  const columns = [
    { field: 'rackno', headerName: 'Rack No' },
    { field: 'rackdesc', headerName: 'Rack Description' },
  ]




  const [errors, setErrors] = useState({
    warehousecode: false,
    personincharge: false,
    warehousedesc: false,
    warehouseaddress1: false,
    warehouseaddress2: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    warehousecode: '',
    personincharge: '',
    warehousedesc: '',
    warehouseaddress1: '',
    warehouseaddress2: '',
  });


  const token = getAuthToken("id_token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  useEffect(() => {
    if (isEdit) {
      descRef.current.focus();
    } else {
      serviceRef.current.focus();
    }
  }, [isEdit]);


  const facilityid = getFacilityId('facilityid');
  const createdBy = getCreatedBy('createdBy');


  useEffect(() => {
    const fetchServiceOptions = async () => {
      try {
        const response = await axios.get(`${MasterGraph}/api/graph/GetExportData/${facilityid}`, config);
        const warehouseDetails = response.data.storeDetailsVMs;

        if (Array.isArray(warehouseDetails)) {
          const options = warehouseDetails.map(detail => ({
            value: detail.warehousedesc,
            label: detail.warehousecode,
            id: detail.id
          }));
          setServiceOptions(options);
        } else {
          console.error("warehouseDetails is not an array");
        }

        if (location.state) {
          const {
            warehousecode, warehousedesc, personincharge, warehouseaddress1, warehouseaddress2,
            warehousecity, warehousestate, warehousecountry, warehousepostcode,
            warehousephoneno, warehousefaxno, warehouseemail, id,
            readOnly, readOnlyEdit, mode,
          } = location.state;

          setWarehousecode(warehousecode);
          setPersonincharge(personincharge);
          setWarehousedesc(warehousedesc);
          setWarehouseaddress1(warehouseaddress1);
          setWarehouseaddress2(warehouseaddress2);
          setWarehousecity(warehousecity);
          setWarehousestate(warehousestate);
          setWarehousecountry(warehousecountry);
          setWarehousepostcode(warehousepostcode);
          setWarehousephoneno(warehousephoneno);
          setWarehousefaxno(warehousefaxno);
          setWarehouseemail(warehouseemail);
          setId(id);
          setReadOnly(!!readOnly);
          setReadOnlyEdit(!!readOnlyEdit);
          if (mode === "edit") {
            setIsEdit(true);
            setIsView(false);
          } else if (mode === "view") {
            setIsView(true);
            setIsEdit(false);
          } else {
            setIsEdit(false);
            setIsView(false);
          }
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };

    fetchServiceOptions();
  }, [location.state]);

  const handleSubmit = (e) => {
    e.preventDefault();

    let formIsValid = true;

    if (!warehousecode) {
      formIsValid = false;
      setErrors((prevErrors) => ({ ...prevErrors, warehousecode: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecode: 'Store Code is required' }));


    }

    if (!personincharge) {
      formIsValid = false;
      setErrors((prevErrors) => ({ ...prevErrors, personincharge: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, personincharge: 'Person-Incharge is required' }));

    }
    if (!warehousedesc) {
      formIsValid = false;
      setErrors((prevErrors) => ({ ...prevErrors, warehousedesc: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousedesc: 'Description is required' }));
    }
    if (!warehouseaddress1) {
      formIsValid = false;
      setErrors((prevErrors) => ({ ...prevErrors, warehouseaddress1: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseaddress1: 'Address 1 is required' }));

    }
    if (!Object.values(errorMessages).every(value => value === '')) {
      formIsValid = false
    }

    if (formIsValid) {
      handleSave();
    }
  };



  const handleCancel = () => {
    if (!isChanged) {
      navigate(-1, { replace: true });
    }
    else {
      setCancelDialogOpen(true)
      setDialogContent("Record(s) not Saved/Updated. Do you want to continue?")
    }
  };
  const formData = {
    warehousecode,
    personincharge,
    warehousedesc,
    warehouseaddress1,
    warehouseaddress2,
    warehousecity,
    warehousestate,
    warehousecountry,
    warehousepostcode,
    warehousephoneno,
    warehousefaxno,
    warehouseemail,

  }
  const areAllEmpty = () => {
    for (const key in formData) {
      if (Array.isArray(formData[key])) {
        if (formData[key].length !== 0) {
          return false;
        }
      } else if (formData[key]) {
        return false;
      }
    }
    return true;
  };
  const handleAddNew = () => {
    if (isChanged && !areAllEmpty()) {
      setIsAddNew(true);
      setCancelDialogOpen(true);
      setDialogContent("Record(s) not Saved/Updated. Do you want to continue?");
    } else {
      handleAdd();
    }
  };
  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
  };

  const handleConfirmCancel = () => {
    setCancelDialogOpen(false)
    setOpenValidationDialog(false)
    if (isAddNew) {
      handleAdd()
      setIsAddNew(false)
      setIsChanged(false)
    }

    if (isChanged && !isAddNew && !openValidationDialog) {
      setCancelDialogOpen(false);
      navigate(-1, { replace: true });
    }

  };

  const handleDialogClose = () => {
    if (isChanged) {
      setOpenValidationDialog(false);
    }
    if (isAddNew) {
      setIsAddNew(false);
    }
    setCancelDialogOpen(false);
    setOpenValidationDialog(false)
  };
  const handlewarehousecodeChange = (newValue) => {
    setIsChanged(true);
    setWarehousecode(newValue);

    const regex = /^[a-zA-Z0-9 /-]*$/;

    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousecode: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecode: 'Invalid Characters in Store Code' }));
    } else if (newValue.startsWith(' ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousecode: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecode: 'Space not allowed at the beginning' }));
    } else if (newValue.endsWith(' ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousecode: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecode: 'Space not allowed at the end' }));
    } else if (newValue.includes('  ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousecode: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecode: 'Double Space not allowed' }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, warehousecode: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecode: '' }));
    }
  };

  const handlepersoninchargeChange = (newValue) => {
    setIsChanged(true);
    setPersonincharge(newValue);

    const regex = /^[a-zA-Z0-9 -]*$/;

    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, personincharge: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, personincharge: 'Invalid Characters in Person-In Charge' }));
    } else if (newValue.startsWith(' ')) {
      setErrors((prevErrors) => ({ ...prevErrors, personincharge: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, personincharge: 'Space not allowed at the beginning' }));
    } else if (newValue.includes('  ')) {
      setErrors((prevErrors) => ({ ...prevErrors, personincharge: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, personincharge: 'Double Space not allowed' }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, personincharge: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, personincharge: '' }));
    }
  };

  const handlewarehousedescChange = (newValue) => {
    setIsChanged(true);
    setWarehousedesc(newValue);

    const regex = /^[a-zA-Z0-9,!@#$%&*()_\- ]*$/;

    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousedesc: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousedesc: 'Invalid Characters in Description' }));
    } else if (newValue.startsWith(' ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousedesc: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousedesc: 'Space not allowed at the beginning' }));
    } else if (newValue.includes('  ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousedesc: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousedesc: 'Double Space not allowed' }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, warehousedesc: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousedesc: '' }));
    }
  };
  const handlewarehouseaddress1Change = (newValue) => {
    setIsChanged(true);
    setWarehouseaddress1(newValue);

    const regex = /^[a-zA-Z0-9,!@#$%&*()_\-. ]*$/;

    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, warehouseaddress1: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseaddress1: 'Invalid Characters in Address1' }));
    } else if (newValue.startsWith(' ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehouseaddress1: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseaddress1: 'Space not allowed at the beginning' }));
    } else if (newValue.includes('  ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehouseaddress1: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseaddress1: 'Double Space not allowed' }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, warehouseaddress1: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseaddress1: '' }));
    }
  };
  const handlewarehouseaddress2Change = (newValue) => {
    setIsChanged(true);
    setWarehouseaddress2(newValue);

    const regex = /^[a-zA-Z0-9,!@#$%&*()_\-. ]*$/;
    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, warehouseaddress2: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseaddress2: 'Invalid Characters in Address2' }));
    } else if (newValue.startsWith(' ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehouseaddress2: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseaddress2: 'Space not allowed at the beginning' }));
    } else if (newValue.includes('  ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehouseaddress2: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseaddress2: 'Double Space not allowed' }));
    }
    else {
      setErrors((prevErrors) => ({ ...prevErrors, warehouseaddress2: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseaddress2: '' }));
    }
  };
  const handlewarehousestateChange = (newValue) => {
    setIsChanged(true);
    setWarehousestate(newValue);
    const regex = /^[a-zA-Z0-9 -]*$/;
    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousestate: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousestate: 'Invalid Characters in State' }));
    } else if (newValue.startsWith(' ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousestate: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousestate: 'Space not allowed at the beginning' }));
    } else if (newValue.includes('  ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousestate: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousestate: 'Double Space not allowed' }));
    }
    else {
      setErrors((prevErrors) => ({ ...prevErrors, warehousestate: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousestate: '' }));
    }
  };

  const handlewarehousecountryChange = (newValue) => {
    setIsChanged(true);
    setWarehousecountry(newValue);

    const regex = /^[a-zA-Z0-9 -]*$/;
    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousecountry: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecountry: 'Invalid Characters in Country' }));
    } else if (newValue.startsWith(' ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousecountry: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecountry: 'Space not allowed at the beginning' }));
    } else if (newValue.includes('  ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousecountry: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecountry: 'Double Space not allowed' }));
    }
    else {
      setErrors((prevErrors) => ({ ...prevErrors, warehousecountry: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecountry: '' }));
    }
  };

  const handlewarehousecityChange = (newValue) => {
    setIsChanged(true);
    setWarehousecity(newValue);
    const regex = /^[a-zA-Z0-9 -]*$/;
    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousecity: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecity: 'Invalid Characters in City' }));
    } else if (newValue.startsWith(' ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousecity: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecity: 'Space not allowed at the beginning' }));
    } else if (newValue.includes('  ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousecity: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecity: 'Double Space not allowed' }));
    }
    else {
      setErrors((prevErrors) => ({ ...prevErrors, warehousecity: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousecity: '' }));
    }
  };
  const handlewarehousephonenoChange = (newValue) => {
    setIsChanged(true);
    setWarehousephoneno(newValue);

    const regex = /^[0-9+]*$/;
    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousephoneno: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousephoneno: 'Invalid Characters in Telephone No' }));
    } else if (newValue.startsWith(' ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousephoneno: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousephoneno: 'Space not allowed at the beginning' }));
    } else if (newValue.includes('  ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousephoneno: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousephoneno: 'Double Space not allowed' }));
    }
    else {
      setErrors((prevErrors) => ({ ...prevErrors, warehousephoneno: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousephoneno: '' }));
    }
  };
  const handlewarehousepostcodeChange = (newValue) => {
    setIsChanged(true);
    setWarehousepostcode(newValue);
    const regex = /^[0-9]*$/;
    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousepostcode: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousepostcode: 'Invalid Characters in Postal code' }));
    } else if (newValue.startsWith(' ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousepostcode: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousepostcode: 'Space not allowed at the beginning' }));
    } else if (newValue.includes('  ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousepostcode: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousepostcode: 'Double Space not allowed' }));
    }
    else {
      setErrors((prevErrors) => ({ ...prevErrors, warehousepostcode: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousepostcode: '' }));
    }
  };
  const handlewarehousefaxnoChange = (newValue) => {
    setIsChanged(true);
    setWarehousefaxno(newValue);
    const regex = /^[0-9]*$/;
    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousefaxno: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousefaxno: 'Invalid Characters in Fax No' }));
    } else if (newValue.startsWith(' ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousefaxno: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousefaxno: 'Space not allowed at the beginning' }));
    } else if (newValue.includes('  ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehousefaxno: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousefaxno: 'Double Space not allowed' }));
    }

    else {
      setErrors((prevErrors) => ({ ...prevErrors, warehousefaxno: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehousefaxno: '' }));
    }
  };
  // const handlewarehouseemailChange = (newValue) => {
  //   setIsChanged(true);
  //   setWarehouseemail(newValue);

  //   const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  //   if (!regex.test(newValue)) {
  //     setErrors((prevErrors) => ({ ...prevErrors, warehouseemail: true }));
  //     setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseemail: 'Invalid Characters in Email' }));
  //   } else if (newValue.startsWith(' ')) {
  //     setErrors((prevErrors) => ({ ...prevErrors, warehouseemail: true }));
  //     setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseemail: 'Space not allowed at the beginning' }));
  //   } else if (newValue.includes('  ')) {
  //     setErrors((prevErrors) => ({ ...prevErrors, warehouseemail: true }));
  //     setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseemail: 'Double Space not allowed' }));
  //   }
  //   else {
  //     setErrors((prevErrors) => ({ ...prevErrors, warehouseemail: false }));
  //     setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseemail: '' }));
  //   }
  // };
  const handlewarehouseemailChange = (newValue) => {
    setIsChanged(true);
    setWarehouseemail(newValue);

    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (newValue === '') {
      // Clear errors and messages if the input is empty
      setErrors((prevErrors) => ({ ...prevErrors, warehouseemail: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseemail: '' }));
    } else if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, warehouseemail: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseemail: 'Invalid Email format' }));
    } else if (newValue.startsWith(' ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehouseemail: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseemail: 'Space not allowed at the beginning' }));
    } else if (newValue.includes('  ')) {
      setErrors((prevErrors) => ({ ...prevErrors, warehouseemail: true }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseemail: 'Double Space not allowed' }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, warehouseemail: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, warehouseemail: '' }));
    }
  };

  const fetchrackdetails = async () => {
    try {
      const response = await axios.get(
        `${MasterGraph}/api/masimsregiondetails/Rackdetailslist/${id}`,
        config
      );

      let rackDetails = response.data;

      if (!Array.isArray(rackDetails)) {
        rackDetails = [rackDetails];
      }

      setRows(rackDetails);
    } catch (error) {
      console.error("Error fetching Rack details:", error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchrackdetails();
    }
  }, [id]);

  const handleSave = async () => {
    try {

      const payload = {
        id,
        facilityid: facilityid,
        warehousecode: warehousecode,
        warehousedesc: warehousedesc,
        personincharge: personincharge,
        warehouseaddress1: warehouseaddress1,
        warehouseaddress2: warehouseaddress2,
        warehousecity: warehousecity,
        warehousestate: warehousestate,
        warehousecountry: warehousecountry,
        warehousepostcode: warehousepostcode,
        warehousephoneno: warehousephoneno,
        warehousefaxno: warehousefaxno,
        warehouseemail: warehouseemail,
        createdby: createdBy,
        createddate: new Date().toISOString(),
        isdeleted: false
      };

      let response;
      if (isEdit) {
        //  payload.id = id;
        response = await axios.put(`${MasterGraph}/api/masimswarehousedetails/${id}`, payload, config);
      } else {
        response = await axios.post(`${MasterGraph}/api/masimswarehousedetails`, payload, config);
      }
      // response=  await axios.post(`https://localhost:7072/api/masimswarehousedetails`, payload, config);
      if (response.status == 201) {
        setOpenValidationDialog(true)
        setDialogContent('Data saved successfully.')
        setIsChanged(false)
      }
      else if (response.status == 200) {
        setOpenValidationDialog(true)
        setDialogContent('Data updated successfully.')
        setIsChanged(false)
      }

      //  setIsSubmitted(true);
      // setDialogOpen(true);
    } catch (error) {
      console.error("Error saving Store details:", error);
      if (error.response.status === 400) {

        setDialogContent(" Store Code is Already Exists");

      } else {
        setDialogContent("An unexpected error occurred. Please try again later.");
      }
      setOpenValidationDialog(true);

    }
  };

  const handleAdd = () => {
    setWarehousecode('');
    setPersonincharge('');
    setWarehousedesc('');
    setWarehouseaddress1('');
    setWarehouseaddress2('');
    setWarehousecity('');
    setWarehousestate('');
    setWarehousecountry('');
    setWarehousepostcode('');
    setWarehousephoneno('');
    setWarehousefaxno('');
    setWarehouseemail('');

    setIsEdit(false);
    setIsView(false);

    setErrors({ warehousecode: false, personincharge: false, warehousedesc: false, warehouseaddress1: false });
    setErrorMessages({ warehousecode: '', personincharge: '', warehousedesc: '', warehouseaddress1: '' });
  };

  return (
    <>
      <PageTitle
        isEdit={isEdit}
        isView={isView}
        editTitle={"Edit Store Details"}
        viewTitle={" View Store Details"}
        title={"Create Store Details"}
      />
      <Box mt={{ xs: 1, md: 2 }}>
        <DefaultCard sx={{ height: "100%" }}>
          <form onSubmit={handleSubmit} style={{ position: "relative" }}>
            <Box mb={2}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={4}>
                  <CustomTextField
                    maxlength={16}
                    id="warehousecode-label"
                    label="Store Code"
                    value={warehousecode}
                    onChange={handlewarehousecodeChange}
                    disabled={readOnly || readOnlyEdit}
                    isMandatoryField={true}
                    error={errors.warehousecode}
                    errorMessage={errorMessages.warehousecode}
                    inputRef={serviceRef}
                  />
                </Grid>

                <Grid item xs={6} md={4}>
                  <CustomTextField
                    //  autoFocus={true}

                    maxlength={60}
                    label="Description"
                    id="asset-category-warehousedesc"
                    value={warehousedesc}
                    onChange={handlewarehousedescChange}
                    disabled={readOnly}
                    isMandatoryField={true}
                    error={errors.warehousedesc}
                    errorMessage={errorMessages.warehousedesc}
                    inputRef={descRef}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <CustomTextField
                    maxlength={20}
                    label="Person-Incharge"
                    id="person"
                    value={personincharge}
                    onChange={handlepersoninchargeChange}
                    disabled={readOnly}
                    isMandatoryField={true}
                    error={errors.personincharge}
                    errorMessage={errorMessages.personincharge}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <CustomTextField
                    maxlength={120}
                    label="Address 1"
                    id="asset-category-warehouseaddress1"
                    value={warehouseaddress1}
                    onChange={handlewarehouseaddress1Change}
                    disabled={readOnly}
                    isMandatoryField={true}
                    error={errors.warehouseaddress1}
                    errorMessage={errorMessages.warehouseaddress1}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <CustomTextField
                    maxlength={120}
                    label="Address 2"
                    id="asset-category-Address2"
                    value={warehouseaddress2}
                    onChange={handlewarehouseaddress2Change}
                    disabled={readOnly}
                    error={errors.warehouseaddress2}
                    errorMessage={errorMessages.warehouseaddress2}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <CustomTextField
                    maxlength={20}
                    label="State "
                    id="state"
                    value={warehousestate}
                    onChange={handlewarehousestateChange}
                    disabled={readOnly}
                    error={errors.warehousestate}
                    errorMessage={errorMessages.warehousestate}

                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <CustomTextField
                    maxlength={20}
                    label="Country"
                    id="country"
                    value={warehousecountry}
                    onChange={handlewarehousecountryChange}
                    disabled={readOnly}
                    error={errors.warehousecountry}
                    errorMessage={errorMessages.warehousecountry}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <CustomTextField
                    maxlength={20}
                    label="City"
                    id="city"
                    value={warehousecity}
                    onChange={handlewarehousecityChange}
                    disabled={readOnly}
                    error={errors.warehousecity}
                    errorMessage={errorMessages.warehousecity}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <CustomTextField
                    maxlength={20}
                    label="Telephone No."
                    id="asset-category-description"
                    value={warehousephoneno}
                    onChange={handlewarehousephonenoChange}
                    disabled={readOnly}
                    error={errors.warehousephoneno}
                    errorMessage={errorMessages.warehousephoneno}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <CustomTextField
                    maxlength={20}
                    label="Postal Code"
                    id="asset-category-description"
                    value={warehousepostcode}
                    onChange={handlewarehousepostcodeChange}
                    disabled={readOnly}
                    error={errors.warehousepostcode}
                    errorMessage={errorMessages.warehousepostcode}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <CustomTextField
                    maxlength={20}
                    label="Fax No."
                    id="asset-category-description"
                    value={warehousefaxno}
                    onChange={handlewarehousefaxnoChange}
                    disabled={readOnly}
                    error={errors.warehousefaxno}
                    errorMessage={errorMessages.warehousefaxno}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <CustomTextField
                    maxlength={30}
                    label="Email"
                    id="asset-category-description"
                    value={warehouseemail}
                    onChange={handlewarehouseemailChange}
                    disabled={readOnly}
                    error={errors.warehouseemail}
                    errorMessage={errorMessages.warehouseemail}
                  />
                </Grid>
              </Grid>

            </Box>
            {(isEdit || isView) &&
              <Box>
                <RackDetails
                  storeid={id}
                  rows={rows}
                  columns={columns}
                  fetchrackdetails={fetchrackdetails}
                  isviewmode={isView} />
              </Box>
            }
          </form>
          <CustomFooter
            cancelClick={handleCancel}
            saveClick={handleSubmit}
            addClick={handleAddNew}
            readOnly={readOnly}
            buttonLabel={isEdit ? "Update" : "Save"}
            isEdit={isEdit}
            showAddButton={true}
          />

        </DefaultCard>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: { xs: "center", md: "end" },
          }}
        >

        </Box>
      </Box>

      <CustomDialog
        open={cancelDialogOpen}
        onConfirm={handleConfirmCancel}
        onClose={handleDialogClose}
        content={dialogContent}
        confirmBtn={'Yes'}
        cancelBtn={'No'}
      />
      <CustomDialog
        open={openValidationDialog}
        onConfirm={handleConfirmCancel}
        onClose={handleDialogClose}
        content={dialogContent}
        confirmBtn={'Ok'}

      />
    </>
  );
};

export default CreateStoreDetails;
